import {
  deriveKeyFromName,
  displayNameForKey,
  isValidConfigKey,
} from "./StaticDataEditor.support";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import TextField from "components/common/TextField";
import { useEffect, useState } from "react";

const NewConfigDialog = ({
  open,
  onClose,
  onCreate,
  initialInput,
  existingKeys,
}) => {
  const [displayName, setDisplayName] = useState("");
  const [key, setKey] = useState("");
  const [keyEdited, setKeyEdited] = useState(false);

  useEffect(() => {
    if (open) {
      const input = (initialInput ?? "").trim();
      setDisplayName(input);
      setKey(deriveKeyFromName(input));
      setKeyEdited(false);
    }
  }, [open, initialInput]);

  const keyValid = isValidConfigKey(key);
  const keyTaken = keyValid && (existingKeys ?? []).includes(key);
  const shownAs = keyValid ? displayNameForKey(key) : "";
  const nameDiffers =
    displayName.trim() !== "" &&
    shownAs !== "" &&
    displayName.trim() !== shownAs;

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth={"sm"}>
      <DialogTitle>{"Create new config"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label={"Display name"}
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (!keyEdited) setKey(deriveKeyFromName(e.target.value));
              }}
              helperText={"What people should call it, e.g. “My Test Config”"}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={"Key"}
              value={key}
              onChange={(e) => {
                setKey(e.target.value);
                setKeyEdited(true);
              }}
              error={(key !== "" && !keyValid) || keyTaken}
              helperText={
                keyTaken
                  ? "A config with this key already exists — select it from the list instead"
                  : key !== "" && !keyValid
                  ? "Letters, digits, underscores and hyphens only, starting with a letter or digit"
                  : "The identifier the config is stored under, e.g. my_test_config"
              }
            />
          </Grid>
          {shownAs !== "" && !keyTaken && (
            <Grid item xs={12}>
              <Typography variant={"body2"}>
                {"Will appear in the panel as: "}
                <b>{shownAs}</b>
              </Typography>
            </Grid>
          )}
          {nameDiffers && !keyTaken && (
            <Grid item xs={12}>
              <Alert severity={"info"}>
                {`Display names are derived from the key, so the panel will show “${shownAs}”. Showing “${displayName.trim()}” instead needs a display_name entry for this key in the config registry (a small code change).`}
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color={"secondary"} onClick={() => onClose()}>
          {"Cancel"}
        </Button>
        <Button
          isDisabled={!keyValid || keyTaken}
          onClick={() => onCreate(key)}
        >
          {"Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewConfigDialog;
